import { AutoAwesome, Close, DeleteOutline, EditOutlined, KeyboardBackspace, Visibility } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid2 as Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DataSheetPDFPreview,
  HighlightSelectBox,
  MyPaper,
  SectionPaper,
} from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch } from "../../../redux";
import { constructRow, useChecklistResponseMutation, useCreateDataTableMutation, useDeleteDataTableMutation, useLazyGetAllDataTableDDOptionsQuery, useLazyGetDataTableByIdQuery, useUpdateDataTableMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog } from "../../../redux/slices/confirmationDialog";
import { closeDialog, openDialog } from "../../../redux/slices/dialog";
import { Categories } from "../../viewCheckList/categories";
import { CheckList, CheckListContext, giveMeInitialState } from "../../viewCheckList/viewCheckListDetail";
import { ImportDSFromDoc } from "./importDSFromDoc";
import CustomTable from "./table";

type LinkDataTableTemplate = (links: any[], unlinks: any[]) => Promise<void>;

export const DataTableForm: React.FC<{
  type: "datasheet" | "inputParameter" | "checklist",
  refetch?: () => void,
  linkDataTableTemplate: LinkDataTableTemplate,
  getPath?: (path: string) => string,
  sheet_id?: string,
  preview?: boolean,
  dataTableData?: any,
  associations?: any[],
  info?: string,
  hideHeader?: boolean,
  hideAutoPopulate?: boolean,
  otherDetails?: { searchId?: string }, sequenceId?: string, onClose?: () => void, forDocument?: boolean
}> = (props) => {

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  let { ds_id = "New" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const theme = useTheme();
  const isIpadOrMobileMatch = useMediaQuery(theme.breakpoints.down("md"));

  const [getAllDataTable, { data: publishedTemplateOptions = [], isFetching, isLoading, status }] = useLazyGetAllDataTableDDOptionsQuery({ refetchOnFocus: true, refetchOnReconnect: true });
  const [getDataTableById, dataTableResponse] = useLazyGetDataTableByIdQuery({ refetchOnFocus: true, refetchOnReconnect: true })
  const [createForm, formResponse] = useCreateDataTableMutation();
  const [updateForm, updatedFormResponse] = useUpdateDataTableMutation();
  const [deleteForm] = useDeleteDataTableMutation();
  const [checklistRes] = useChecklistResponseMutation();

  const [selectedTemplate, setSelectedTemplate] = React.useState<any>(null);
  const [formId, setFormId] = React.useState(null);
  const [state, updateState] = React.useState<any[]>([]);
  const [entireState, setEntireState] = React.useState<CheckList & { document_id?: string }>(giveMeInitialState());
  const [dataSheetCreateMode, setDataSheetCreateMode] = React.useState<'' | 'dataSheetTemplate' | 'customDatasheet' | 'aiExtractor' | string>('');

  const editMode = searchParams.get("mode") === "edit" && !props.preview;

  if (props?.sheet_id) {
    ds_id = props?.sheet_id
  }

  const isSystemGenId = ds_id !== "New";

  const loading = isFetching || isLoading || dataTableResponse.isFetching || dataTableResponse.isLoading || formResponse.isLoading || updatedFormResponse.isLoading;

  const handleDataSheetCreateMode = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDataSheetCreateMode((event.target as HTMLInputElement).value);
  };

  const massageData = (prevState: CheckList, data: any) => {
    return {
      ...prevState, ...data, status, tags_info: data?.tags_info?.map((_: any) => ({ ..._, value: _.tag_id, label: _.tag_name })), categories: data?.tables?.map((table: any, index: number) => {
        return {
          ...table,
          name: table.name,
          position: index,
          description: table.description,
          definition: {
            columns: table?.columns?.filter((col: any) => col.field)?.map((col: any) => {
              return {
                ...col,
                headerName: col?.header_name || "",
                headerClassName: col?.header_class_name || "",
              }
            }),
            rows: table?.rows?.map((row: any) => {
              const obj: any = {};
              row?.column_content?.forEach((col: any) => {
                obj[col.field] = col.content
              })
              return {
                id: row?.id,
                ...obj
              }
            })
          }
        }
      })
    };
  };

  const updateFormState = (key: string, value: any) => {
    setEntireState((prevEState) => ({ ...prevEState, [key]: value }));
  };

  const fetchTemplates = async () => {
    dispatch(openBackdrop(`Fetching ${props.type === 'datasheet' ? 'Data Sheet' : 'Data Table'} Templates...`));
    await getAllDataTable({ dtTemplateType: props.type }).unwrap().finally(() => {
      dispatch(closeBackdrop());
    })
  };

  const updateTablesState = async (res: any) => {
    const newState = res?.tables?.map((table: any, idx: number) => ({
      name: table.name,
      position: idx,
      description: table.description,
      header_color: table?.header_color,
      definition: {
        columns: table?.columns?.filter((col: any) => col.field)?.map((col: any) => {
          return {
            ...col,
            headerName: col.header_name,
            headerClassName: `${col.header_class_name}`,
            resizable: false,
            sortable: false,
            disableColumnMenu: true,
          }
        }),
        rows: table?.rows?.map((row: any) => {
          const obj: any = {};
          row?.column_content?.forEach((col: any) => {
            obj[col.field] = col.content
          })
          return {
            id: row?.id,
            ...obj
          }
        }) || [],
      },
    }));
    updateState(newState)
  };

  const fetchDataTable = async () => {
    if (isSystemGenId) {
      if (!props.preview && props.type === "checklist") {
        dispatch(openBackdrop(`Fetching Check List Detail...`));
      };
      await getDataTableById({
        dtTemplateType: props.type,
        dtInstanceType: "form",
        dtTemplateId: ds_id
      }).unwrap().then(async (res) => {
        if (res) {
          await updateTablesState(res);
          setFormId(res.sequence_id);
          setSelectedTemplate(publishedTemplateOptions.find(template => res.template_id === template.value) || null)
          setEntireState(massageData(entireState, res));
        }
      }).catch((error: any) => {
        console.error(`${props.type} form fetch error: `, error)
      }).finally(() => {
        dispatch(closeBackdrop());
      })
    }
  };

  const onSuccessfullyFormCreation = async (res: any, isEdit?: boolean) => {
    enqueueSnackbar('Form Created Successfully', { variant: "success" });
    await props.linkDataTableTemplate([res?._id], [])
    if (props.getPath) {
      navigate(props.getPath(res?._id) + `${isEdit ? '?mode=edit' : ''}`);
    }
    setFormId(res?.sequence_id);
  };

  const onCreateForm = async (isCustom: boolean) => {
    if (selectedTemplate?._id) {

      if (props.type === 'checklist') {

        const errorMessages: { table: string, messages: string[] }[] = [];

        if (selectedTemplate?.tables?.length) {
          selectedTemplate?.tables?.forEach((table: any, index: number) => {
            let isDescriptionColumnExists: boolean = false;
            let isDescriptionColumnNotEditableExists: boolean = false;
            let isResponseColumnExists: boolean = false;
            let isResponseColumnEditableExists: boolean = false;
            let isNotesColumnExists: boolean = false;
            let isNotesColumnEditableExists: boolean = false;

            const messages: string[] = [];

            table?.columns?.forEach((col: any, index: number) => {
              if (col.header_name === "Description") {
                isDescriptionColumnExists = true;
                if (!col.editable) {
                  isDescriptionColumnNotEditableExists = true;
                }
              }

              if (col.header_name === 'Response') {
                isResponseColumnExists = true;
                if (col.editable) {
                  isResponseColumnEditableExists = true;
                }
              }

              if (col.header_name === 'Notes') {
                isNotesColumnExists = true;
                if (col.editable) {
                  isNotesColumnEditableExists = true;
                }
              }
            })

            if (!isDescriptionColumnExists) {
              messages.push(`'Description' column is not found in the table.`)
            }

            if (!isDescriptionColumnNotEditableExists) {
              messages.push(`'Description' column should not be editable.`)
            }

            if (!isResponseColumnExists) {
              messages.push(`'Response' column is nout found in the table.`)
            }

            if (!isNotesColumnExists) {
              messages.push(`'Notes' column is not found in the table.`)
            }

            if (!isResponseColumnEditableExists) {
              messages.push(`'Response' column should be editable.`)
            }

            if (!isNotesColumnEditableExists) {
              messages.push(`'Response' column should be editable.`)
            }

            if (messages.length) {
              errorMessages.push({ table: table.name, messages })
            }

          })
        } else {
          errorMessages.push({ table: '', messages: ['No tables are mapped to the selected checklist'] })
        }

        if (errorMessages.length) {
          dispatch(openDialog({
            title: "Checklist Validation Errors",
            hideNegativeBtn: true,
            hideCloseIcon: true,
            body: <Stack sx={{
              gap: 1
            }}>
              <Alert severity="info" >{props.info}</Alert>

              <Typography variant="subtitle1"><b>Errors: </b></Typography>

              {errorMessages.map((errorMessage, index) => {
                return <Box key={index}>
                  <Typography variant="subtitle2" sx={{ textDecoration: "underline" }}>{errorMessage.table}</Typography>

                  <ul>
                    {errorMessage.messages.map((message, index) => {
                      return <Typography key={"index"} component={"li"} variant="body2">{message}</Typography>
                    })}
                  </ul>
                </Box>
              })}

              <Typography variant="body1">Please add a checklist the follows the above validations.</Typography>
            </Stack>,
            onOk: () => dispatch(closeDialog())
          }))
          return false;
        }
      }

      dispatch(openBackdrop("Creating Form..."));
      await createForm({
        dtTemplateType: props.type,
        dtInstanceType: "form",
        dtTemplateId: selectedTemplate?._id,
        payload: {
          name: selectedTemplate?.name,
          description: selectedTemplate?.description || undefined,
          tables: selectedTemplate?.tables?.map((table: any, index: number) => {
            return {
              name: table.name,
              description: table.description,
              position: index,
              rows: table?.rows ?? [],
              header_color: table?.header_color,
              columns: table?.columns?.filter((col: any) => col.field)?.map((col: any) => {
                return {
                  ...col,
                  header_class_name: col.header_class_name,
                  header_name: col.header_name,
                  editable: col?.editable ? true : false
                };
              }) ?? [],
            }
          }) ?? [],
        } as any
      }).unwrap().then(async (res) => {
        if (!res.title) {
          onSuccessfullyFormCreation(res);
        }
      }).catch((error: any) => {
        enqueueSnackbar(
          error?.data?.title || "Something went wrong unable to create form",
          { variant: "error" }
        );
      }).finally(() => {
        dispatch(closeBackdrop());
      })
    }

    if (isCustom) {

      dispatch(openBackdrop("Creating Form..."));
      const templateName = `Datasheet of ${props.sequenceId}`;

      await createForm({
        dtTemplateType: props.type,
        dtInstanceType: "form",
        dtTemplateId: null,
        payload: {
          name: templateName,
          tables: [],
        } as any
      }).unwrap().then(async (res) => {
        if (!res.title) {
          onSuccessfullyFormCreation(res, true);
        }
      }).catch((error: any) => {
        enqueueSnackbar(
          error?.data?.title || "Something went wrong unable to create form",
          { variant: "error" }
        );
      }).finally(() => {
        dispatch(closeBackdrop());
      })
    }
  };

  const onUpdate = async (reset: () => void, data?: CheckList & { document_id?: string }, showMessage?: boolean) => {
    const payloadState = data?.categories || state;
    if (
      formId
    ) {
      if (!props.preview && props.type === 'checklist') {
        dispatch(openBackdrop("Updating Form..."));
      }
      await updateForm({
        dtTemplateType: props.type,
        dtInstanceType: "form",
        dtTemplateId: ds_id,
        queryTemplateId: selectedTemplate?._id,
        payload: {
          document_id: data?.document_id || entireState?.document_id,
          name: selectedTemplate?.name || data?.name || entireState?.name,
          description: selectedTemplate?.description || data?.description || entireState?.description || undefined,
          tables: payloadState?.map((table: any, index: number) => {
            return {
              name: table.name,
              description: table.description,
              position: index,
              header_color: table?.header_color,
              rows: table?.definition?.rows?.map((row: any) => ({
                column_content: constructRow(table, row),
                id: row?.id,
              })) ?? [],
              columns: table?.definition?.columns?.map((col: any) => {
                return {
                  ...col,
                  header_class_name: col.headerClassName,
                  header_name: col.headerName,
                  editable: col.editable ? true : false
                };
              }) ?? [],
            }
          }) ?? [],
        } as any
      }).unwrap().then((res) => {
        if (!res.title) {
          if (!props.preview && (props.type === 'checklist' || showMessage)) {
            enqueueSnackbar('Form Updated Successfully', { variant: "success" });
            fetchDataTable();
          }
          if (data?.categories) {
            updateState(data?.categories?.map((d) => ({ ...d, definition: { ...d.definition, columns: d.definition?.columns?.map((c: any) => ({ ...c, header_name: c?.headerName || c?.header_name || '' })) } })))
          }
        }
      }).catch((error: any) => {
        enqueueSnackbar(
          error?.data?.title || "Something went wrong unable to update form",
          { variant: "error" }
        );
      }).finally(() => {
        reset();
        dispatch(closeBackdrop());
      })
    }
  };

  const deleteFormConfirmation = () => {
    dispatch(
      openConfirmationDialog({
        title: "Delete",
        body: `Are you sure you want to delete the ${props.type === "datasheet" ? "data sheet" : "data table"} ${formId}. It will delete the changes you may have done if any.`,
        positiveBtn: "Delete",
        negativeBtn: "Cancel",
        onOk: async () => onTemplateChange(),
        onNegativeBtn: () => dispatch(closeConfirmationDialog()),
      })
    );
  };

  const onTemplateChange = async () => {
    await props.linkDataTableTemplate([], [ds_id]);
    dispatch(openBackdrop("Deleting Form..."))
    await deleteForm({
      dtTemplateType: props.type,
      dtInstanceType: "form",
      dtTemplateId: ds_id
    }).unwrap().then((res) => {
      updateState([]);
      if (props.getPath) {
        navigate(props.getPath("New"));
      }
      setFormId(null);
      setSelectedTemplate(null);
      setEntireState(giveMeInitialState());
      enqueueSnackbar('Form Deleted Successfully', { variant: "success" });
    }).catch((error: any) => {
      enqueueSnackbar(
        error?.data?.title || "unable to delete form",
        { variant: "error" }
      );
    }).finally(() => {
      dispatch(closeBackdrop())
    })
  };

  const openImportFromDocument = async () => {
    dispatch(openDialog({
      title: "Import Datasheet from Documents",
      body: <ImportDSFromDoc forDocument={props.forDocument} associations={props.associations} onSuccessfullyFormCreation={onSuccessfullyFormCreation} />,
      hidePositiveBtn: true,
      hideNegativeBtn: true
    }));
  };

  const openDataTablePreview = async (data: any) => {
    const previewData = {
      ...entireState,
      tables: data?.map((d: any) => ({
        ...d,
        columns: d?.definition?.columns || [],
        rows: (d?.definition?.rows || []).map((r: any) => ({
          id: r.id,
          column_content: Object.keys(r).reduce((acc: Record<string, string>[], curr) => {
            acc.push({ field: curr, content: r[curr] });
            return acc;
          }, []),
        })),
      })),
    };

    const title = entireState?.name ?? "Preview";
    dispatch(
      openDialog({
        title: "",
        hideNegativeBtn: true,
        hideCloseIcon: true,
        maxWidth: "lg",
        enablePadding: false,
        positiveBtn: "Close",
        body: <Stack
          sx={{
            p: 1,
            height: "87vh"
          }}><DataSheetPDFPreview title={title} dataSheet={props.preview ? { data: previewData as any, isLoading: false, isFetching: false, isError: false, isSuccess: true } : { ...dataTableResponse, data: previewData as any }} /></Stack>,
        onOk: () => dispatch(closeDialog())
      })
    );
  };

  const giveMeErrorMessage = () => {
    let message = `Oops! Something went wrong, Unable to Update Datasheet. Try Again Later!`;
    let errorData: any = updatedFormResponse.error;
    message = errorData?.data?.title ?? message;
    return message;
  };

  const onAutoPopulate = async (columns: any[], rows: any[], index: number) => {
    const descriptionKey = columns?.filter(col => col.header_name === 'Description')?.[0]?.field ?? "";
    const responseKey = columns?.filter(col => col.header_name === 'Response')?.[0]?.field ?? "";
    const notesKey = columns?.filter(col => col.header_name === 'Notes')?.[0]?.field ?? "";

    const promisArr = rows.map(row => {
      return checklistRes({ id: `${props?.otherDetails?.searchId ?? ""}`, question: row[descriptionKey] }).unwrap().then(res => {
        return { res, row }
      })
    })

    dispatch(openBackdrop('Auto populating response...'));

    try {
      await Promise.all(promisArr).then((responses: any[]) => {
        responses.forEach(res => {
          state[index].definition.rows = state[index].definition.rows.map((row: any) => {
            if (row.id === res.row.id) {
              return { ...row, [responseKey]: res.res.brief_answer, [notesKey]: res.res.detailed_answer }
            }
            return row;
          })
        })
        updateState(state);
        onUpdate(() => false)
      });
    } catch (error) {
      console.log('error:', error)
      enqueueSnackbar('Something went wrong, Unable to auto generate', { variant: "error" })
    } finally {
      dispatch(closeBackdrop());
    }
  };

  React.useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    fetchDataTable();
    // eslint-disable-next-line
  }, [ds_id, status, editMode]);

  React.useEffect(() => {
    if (!props?.sheet_id && props.dataTableData) {
      const d = props.dataTableData;
      setFormId(d?.sequence_id);
      updateTablesState(d);
      setSelectedTemplate(publishedTemplateOptions.find(template => d?.template_id === template.value) || null);
      setEntireState(massageData(entireState, d));
    }
    // eslint-disable-next-line
  }, [props.dataTableData, publishedTemplateOptions]);

  const canEdit = !props.preview ? true : false;

  const sectionTitle = {
    "datasheet": "Datasheet",
    "inputParameter": "Data Table",
    "checklist": "Checklist Template"
  };

  const isFormCreated = !((!formId && selectedTemplate?._id) || (!selectedTemplate?._id && !formId));

  return <Stack spacing={1} sx={{ position: "relative", mb: 1.2 }}>

    {/* Basic Details */}
    {!props.hideHeader && <SectionPaper
      title={props.forDocument ? "Digital Twin" : sectionTitle[props.type]}
      renderButtons={[
        <>
          {isFormCreated && <Stack sx={{ gap: 1, flexDirection: { sm: "column", md: "row" }, justifyContent: "center", alignItems: "center" }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontFamily: "htrts_medium" }}>
                {`Form ID:`}
              </Typography>
              <Typography variant="body2">{formId}</Typography>
            </Box>

            <Divider orientation={isIpadOrMobileMatch ? "horizontal" : "vertical"} flexItem />

            <Stack sx={{ flexDirection: { sm: "column", md: "row" }, gap: 1 }}>
              {editMode ? <></>
                : canEdit && <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={<EditOutlined />}
                  disabled={loading}
                  onClick={() => setSearchParams({ ...searchParams, mode: "edit" })}
                >Edit</Button>}

              {!editMode && <Tooltip title={"Generate Preview"} arrow placement="top">
                <Button
                  size="small"
                  color="primary"
                  variant="outlined"
                  disabled={loading}
                  onClick={() => openDataTablePreview(state)}
                  endIcon={<Visibility />}
                >
                  Preview
                </Button>
              </Tooltip>}

              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={deleteFormConfirmation}
                startIcon={<DeleteOutline fontSize="small" />}
                disabled={props?.preview || editMode || false}
              >Delete</Button>
            </Stack>
          </Stack>}
          {props.onClose &&
            <IconButton
              aria-label="close"
              onClick={props.onClose}
              sx={{
                backgroundColor: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                outline: "none",
                transition: "box-shadow 0.3s ease-in-out",
                ":hover, :focus": {
                  boxShadow: "0px 0px 5px 2px rgba(103, 58, 183, 0.5)",
                },
                color: (theme) => theme.palette.grey[500],
                width: "25px",
                height: "25px",
                marginTop: "10px",
                marginRight: "11px",
              }}
            >
              <Close />
            </IconButton>
          }

        </>
      ]}
      isExpand={!isFormCreated}
      hideExpandCollapse={true}
    >
      <Grid container spacing={2}>

        {props.type === 'checklist' && <Grid size={{ xs: 12, md: 8 }}>
          <HighlightSelectBox
            disableClearable
            label={"Template"}
            margin={"none"}
            size="small"
            value={publishedTemplateOptions.find(template => selectedTemplate?.value === template.value) || null}
            options={publishedTemplateOptions}
            loading={loading}
            onChange={(data: SelectBoxOption) => {
              setSelectedTemplate(data)
            }}
            disabled={formId || false}
            viewOnly={props.preview}
          />
        </Grid>}

        {props.type === 'checklist' && !props.preview && <Grid size={{ xs: 12, md: 2 }}>
          {!isFormCreated && <LoadingButton
            fullWidth
            size="medium"
            variant="contained"
            onClick={() => onCreateForm(false)}
            loading={loading}
            disabled={props?.preview || editMode || false}
          >
            Create Form
          </LoadingButton>}
        </Grid>}

        {['datasheet', 'inputParameter'].includes(props.type) && !isFormCreated && <>
          <Grid size={{ xs: 12 }}>
            <FormControl>
              <FormLabel >{`Create ${props.forDocument ? "Digital Twin" : sectionTitle[props.type]} Using `}</FormLabel>
              <RadioGroup value={dataSheetCreateMode} onChange={handleDataSheetCreateMode} row>
                <FormControlLabel value="dataSheetTemplate" control={<Radio />} label={`Template`} />
                <FormControlLabel value="customDatasheet" control={<Radio />} label={`Custom Table`} />
                <FormControlLabel value="aiExtractor" control={<Radio />} label="AI Datatable Extractor" />
                {props.forDocument &&
                  (
                    <>
                      <Tooltip title="Coming Soon...">
                        <FormControlLabel disabled value="aiP&IDExtractor" control={<Radio />} label="AI P&ID Extractor" />
                      </Tooltip>
                      <Tooltip title="Coming Soon...">
                        <FormControlLabel disabled value="aiDocumentExtractor" control={<Radio />} label="AI Document Extractor" />
                      </Tooltip>
                      <Tooltip title="Coming Soon...">
                        <FormControlLabel disabled value="aiSLDExtractor" control={<Radio />} label="AI SLD Extractor" />
                      </Tooltip>
                    </>
                  )
                }
              </RadioGroup>
            </FormControl>
          </Grid>

          {dataSheetCreateMode === 'dataSheetTemplate' && <>
            <Grid size={{ xs: 12, md: 8 }}>
              <HighlightSelectBox
                disableClearable
                label={"Template"}
                margin={"none"}
                value={publishedTemplateOptions.find(template => selectedTemplate?.value === template.value) || null}
                options={publishedTemplateOptions}
                loading={loading}
                onChange={(data: SelectBoxOption) => {
                  setSelectedTemplate(data)
                }}
                disabled={formId || false}
                viewOnly={props.preview}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <LoadingButton
                fullWidth
                variant="contained"
                onClick={() => onCreateForm(false)}
                loading={loading}
                disabled={props?.preview || editMode || false}
              >
                Create Form
              </LoadingButton>
            </Grid>
          </>}

          {dataSheetCreateMode === 'customDatasheet' && <Grid size={{ xs: 12, md: 3 }}>
            <Button
              variant="contained"
              disabled={props?.preview || editMode || false}
              fullWidth
              onClick={(e) => onCreateForm(true)}>
              Create Custom Form
            </Button>
          </Grid>}

          {dataSheetCreateMode === 'aiExtractor' && <Grid size={{ xs: 12, md: 3 }}>
            <Button
              variant="contained"
              disabled={props?.preview || editMode || false}
              fullWidth
              startIcon={<AutoAwesome />}
              onClick={(e) => openImportFromDocument()}>
              Load
            </Button>
          </Grid>}
        </>}

        {props.info && <Grid size={12}>
          <Typography variant="caption">{props.info}</Typography>
        </Grid>}
      </Grid>
    </SectionPaper>}

    {/* Main Content */}
    {editMode ?
      <CheckListContext.Provider value={{ state: entireState as CheckList, updateState: updateFormState, error: { name: false, status: false, tags_info: false } }}>
        <Stack spacing={1}>

          <Categories />

          {/* Footer */}
          <MyPaper sx={{ position: 'sticky', bottom: 0 }}>
            <Box>
              {/* Alert Box */}
              {updatedFormResponse.isError && <Alert sx={{ mt: 2 }} severity="error">{
                giveMeErrorMessage()
              }</Alert>}

              {/* Footer Buttons */}
              <Stack
                direction={"row"}
                spacing={1}
                sx={{
                  alignItems: "center",
                  justifyContent: "flex-end",
                  p: 1
                }}>

                {/* Back Button */}
                <Button disabled={loading} variant="outlined" onClick={() => {
                  searchParams.delete("mode")
                  setSearchParams({ ...searchParams })
                }}
                  startIcon={<KeyboardBackspace />}
                >
                  Back
                </Button>

                {/* Create Button */}
                <LoadingButton loading={loading} variant="contained" color="primary" onClick={() => onUpdate(() => { }, entireState, true)}>
                  Update Form
                </LoadingButton>
              </Stack>
            </Box>
          </MyPaper>
        </Stack>
      </CheckListContext.Provider>
      : <>
        {state?.map((data, index: number) => <CustomTable
          onUpdate={onUpdate}
          data={data}
          key={index}
          state={state}
          updateState={updateState}
          index={index}
          preview={props?.preview || false}
          autoPopulate={props.type === 'checklist' ? !props.hideAutoPopulate : false}
          onAutoPopulate={(columns: any[], rows: any) => onAutoPopulate(columns, rows, index)}
        />)}

        {
          (!props.preview && props.type !== "checklist" && formId) && <Typography variant="caption">* Double-click a cell to edit the row, then use the Tab key to move around the table. Press Enter to finish editing.</Typography>
        }
      </>
    }
  </Stack>
};